// Fuzzball Quiz Cart API
// Handles all Shopify cart operations

window.QuizCartAPI = {
  // Remove cart items by quiz ID and source (and optionally cat number)
  async removeCartItemsByQuizId(quizId, source, catNumber = null) {
    try {
      // Get current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Map source to single letter format
      const sourceMap = {
        'bundle': 'B',
        'trail': 'T',
        'extras': 'E'
      };
      const sourceChar = sourceMap[source] || source;
      
      // Find items to remove based on new _qdata format
      let itemsToRemove = cart.items.filter(item => {
        const qdata = item.properties?._qdata;
        if (!qdata) {
          // Fallback to old format if still present
          return item.properties?._quiz_id === quizId && 
                 item.properties?._source === source;
        }
        
        // Parse new format: q{quizId}|{catNumber}|{source}
        const [qid, cat, src] = qdata.split('|');
        const matchesQuiz = qid === `q${quizId}`;
        const matchesSource = src === sourceChar;
        
        return matchesQuiz && matchesSource;
      });
      
      // If cat number specified, filter by that too
      if (catNumber !== null) {
        itemsToRemove = itemsToRemove.filter(item => {
          const qdata = item.properties?._qdata;
          if (!qdata) {
            // Fallback to old format
            const itemCatNumber = item.properties?._cat_number;
            return itemCatNumber === String(catNumber);
          }
          
          // Parse new format
          const [, cat,] = qdata.split('|');
          return cat === String(catNumber);
        });
      }
      
      if (itemsToRemove.length === 0) {
        return;
      }
      
      console.log('Items to remove:', itemsToRemove.map(item => ({
        variant_id: item.variant_id,
        id: item.id,
        key: item.key,
        product_title: item.product_title,
        qdata: item.properties?._qdata
      })));
      
      // Build updates object using item keys
      const updates = {};
      
      // Use the item.key directly as Shopify expects
      itemsToRemove.forEach(item => {
        updates[item.key] = 0;
      });
      
      
      // If no updates, return early
      if (Object.keys(updates).length === 0) {
        return;
      }
      
      // Use the existing updateCart method that works
      const result = await this.updateCart(updates);
      return result;
    } catch (error) {
      throw error;
    }
  },
  
  // Add items to cart
  async addItems(items) {
    
    // Log detailed item info for debugging
    items.forEach((item, index) => {
      console.log(`Item ${index + 1}:`, {
        id: item.id,
        quantity: item.quantity,
        selling_plan: item.selling_plan,
        source: item.source,
        hasProperties: !!item.properties
      });
    });
    
    // Format items for Shopify's bulk add endpoint
    const formData = {
      items: items.map(item => {
        const cartItem = {
          id: item.id,
          quantity: item.quantity
        };
        
        // Add selling plan if available
        if (item.selling_plan) {
          cartItem.selling_plan = item.selling_plan;
        }
        
        // Add properties if available
        if (item.properties) {
          cartItem.properties = item.properties;
        }
        
        return cartItem;
      })
    };
    

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          throw new Error('Cart Error: ' + errorText);
        }
        throw new Error(errorData.message || errorData.description || 'Cart Error');
      }

      const result = await response.json();

      // Set localStorage to indicate quiz was completed (for existing cart modal)
      localStorage.setItem('fuzzball_quiz_completed', 'true');

      return result;
    } catch (error) {
      throw error;
    }
  },

  // Add single item to cart
  async addItem(item) {
    return this.addItems([item]); 
  },

  // Update cart items
  async updateCart(updates) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  },

  // Get current cart
  async getCart() {
    try {
      const response = await fetch('/cart.js');
      
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const cart = await response.json();
      return cart;
    } catch (error) {
      throw error;
    }
  },

  // Remove items by quiz ID
  async removeItemsByQuizId(quizId, sourceType = null) {
    try {
      // First fetch current cart
      const cart = await this.getCart();
      
      // Map source to single letter format if provided
      const sourceMap = {
        'bundle': 'B',
        'trail': 'T',
        'extras': 'E'
      };
      const sourceChar = sourceType ? (sourceMap[sourceType] || sourceType) : null;
      
      // Find items with this quiz ID
      const itemsToRemove = cart.items
        .filter(item => {
          const qdata = item.properties?._qdata;
          
          if (!qdata) {
            // Fallback to old format
            const matchesQuizId = item.properties && item.properties._quiz_id === quizId;
            const matchesSource = !sourceType || item.properties._source === sourceType;
            return matchesQuizId && matchesSource;
          }
          
          // Parse new format: q{quizId}|{catNumber}|{source}
          const [qid, , src] = qdata.split('|');
          const matchesQuizId = qid === `q${quizId}`;
          const matchesSource = !sourceChar || src === sourceChar;
          return matchesQuizId && matchesSource;
        })
        .map(item => ({ id: item.key, quantity: 0 }));
      
      if (itemsToRemove.length === 0) {
        return;
      }
      
      
      // Update cart to remove these items (set quantity to 0)
      const updates = itemsToRemove.reduce((acc, item) => {
        acc[item.id] = 0;
        return acc;
      }, {});
      
      return await this.updateCart(updates);
    } catch (error) {
      // Continue with checkout even if removal fails
    }
  },

  // Build cart items for quiz checkout
  buildQuizCartItems(submissionData, bundleItems, trailItems, extrasItems, isEditMode, editQuizId) {
    console.log('🛒 Building cart items with:', {
      bundleItems: bundleItems.length,
      trailItems: trailItems.length,
      extrasItems: typeof extrasItems === 'object' ? Object.keys(extrasItems).length : extrasItems.length,
      submissionData: submissionData
    });
    
    const cartItems = [];
    const quizId = isEditMode ? editQuizId : `${Date.now()}`;
    
    // Check if this is multi-cat data
    const isMultiCat = submissionData.catCount && submissionData.catCount > 1;
    
    // Get bundle price for subscription items
    const bundlePrice = bundleItems && bundleItems.length > 0 ? this.calculateBundlePrice(bundleItems) : 0;
    
    // Get recommendations data
    const recommendations = window.QuizState.get('recommendations');
    
    // Track carrier products per cat (first bundle item per cat gets full data)
    const carrierProductsByCat = {};
    
    // Group bundle items by cat
    const bundleItemsByCat = {};
    bundleItems.forEach(item => {
      const catNumber = item.cat_number || '1';
      if (!bundleItemsByCat[catNumber]) {
        bundleItemsByCat[catNumber] = [];
      }
      bundleItemsByCat[catNumber].push(item);
    });
    
    // Add bundle items for each cat
    Object.entries(bundleItemsByCat).forEach(([catNumber, catBundleItems]) => {
      const catData = submissionData[`cat${catNumber}`] || submissionData;
      const catName = catData.fuzzballer_name || `Cat ${catNumber}`;
      
      // Count bundle items for this cat
      let bundleWetCount = 0;
      let bundleDryCount = 0;
      
      catBundleItems.forEach(item => {
        if (item.type === 'wet') bundleWetCount += item.quantity;
        else if (item.type === 'dry') bundleDryCount += item.quantity;
      });
      
      // Build full quiz data for carrier product (first bundle item per cat)
      const fullQuizData = {
        // Customer data
        customer_name: submissionData.customer_name,
        customer_email: submissionData.customer_email,
        
        // Cat data
        cat_name: catName,
        breed: catData.breed,
        age_range: catData.age_range,
        lifestyle: catData.lifestyle,
        wet_dry_preference: catData.wet_dry_preference,
        cans_per_day: catData.cans_per_day || '',
        health_concerns_exists: catData.health_concerns_exists,
        health_concerns: catData.health_concerns,
        disliked_flavors: catData.disliked_flavors || [],

        // Order metadata
        is_multi_cat: isMultiCat,
        cat_count: submissionData.catCount || 1,
        bundle_summary: `${bundleWetCount} packs, ${bundleDryCount} bags`,
        subscription_price: bundlePrice,
        timestamp: Date.now()
      };
      
      // Track if we've assigned the carrier for this cat
      let carrierAssigned = false;
      
      // Add bundle items for this cat
      catBundleItems.forEach((item, index) => {
        // Check if this item has Recharge bundle properties
        const hasRechargeBundleProps = item.properties && item.properties._rc_bundle;
        
        // DEBUG: Log Recharge properties
        if (hasRechargeBundleProps) {
          console.log('🎯 RECHARGE BUNDLE ITEM DETECTED:', {
            variant_id: item.variant_id,
            _rc_bundle: item.properties._rc_bundle,
            _rc_bundle_variant: item.properties._rc_bundle_variant,
            _rc_bundle_parent: item.properties._rc_bundle_parent,
            all_properties: item.properties
          });
        } else {
          console.log('⚠️ NO RECHARGE PROPERTIES on item:', {
            variant_id: item.variant_id,
            item_structure: item
          });
        }
        
        // Determine product type for bundle items - check both 'type' and 'product_type' fields
        const productType = item.type || item.product_type || 'product';
        
        // Build condensed property string with product type
        const qdata = `q${quizId}|${catNumber}|B|${productType}`;
        
        // Determine if this is the carrier product (first bundle item for this cat)
        const isCarrier = index === 0;
        
        // Build properties based on whether this is the carrier
        let itemProperties;
        
        if (isCarrier) {
          // Carrier product gets both condensed and full data
          itemProperties = {
            '_qdata': qdata,
            '_quiz_full_data': JSON.stringify(fullQuizData),
            '_group_title': isMultiCat ? `${catName}'s Monthly Box` : "Your Cat's Monthly Box",
            'Fuzzballer name': catName
          };
          carrierAssigned = true;
          carrierProductsByCat[catNumber] = true;
          console.log(`📦 CARRIER PRODUCT assigned for cat ${catNumber}:`, item.variant_id);
        } else {
          // Non-carrier products get only condensed property
          itemProperties = {
            '_qdata': qdata
          };
        }
        
        // If item has Recharge properties, preserve them
        if (hasRechargeBundleProps) {
          itemProperties = {
            ...item.properties, // Include all Recharge properties (_rc_bundle, etc.)
            ...itemProperties   // Add our condensed properties
          };
        }
        
        // Use the ID from Recharge if available, otherwise use variant_id
        let itemId = hasRechargeBundleProps && item.id ? item.id : String(item.variant_id || item.variantId);
        
        // Extract numeric ID if it's a GraphQL ID
        if (itemId && itemId.startsWith('gid://')) {
          const numericId = itemId.split('/').pop();
          console.log(`🔵 CART API: Converting GraphQL ID to numeric: ${itemId} -> ${numericId}`);
          itemId = numericId;
        }
        
        // Use selling_plan from Recharge if available
        const sellingPlan = hasRechargeBundleProps && item.selling_plan ? 
          item.selling_plan : 
          (item.selling_plan_id || item.selling_plans?.[0]?.plan_id || null);
        
        const cartItem = {
          id: itemId,
          quantity: item.quantity,
          selling_plan: sellingPlan,
          source: 'bundle',
          properties: itemProperties
        };
        
        console.log('📦 FINAL CART ITEM:', {
          id: cartItem.id,
          is_carrier: isCarrier,
          has_rc_bundle: !!itemProperties._rc_bundle,
          properties_count: Object.keys(itemProperties).length,
          qdata: itemProperties._qdata
        });
        
        cartItems.push(cartItem);
      });
    });
    
    // Handle trail items - now per cat
    if (trailItems.length > 0) {
      // Group trail items by cat
      const trailItemsByCat = {};
      trailItems.forEach(item => {
        const catNumber = item.cat_number || '1';
        if (!trailItemsByCat[catNumber]) {
          trailItemsByCat[catNumber] = [];
        }
        trailItemsByCat[catNumber].push(item);
      });
      
      // Add trail items for each cat
      Object.entries(trailItemsByCat).forEach(([catNumber, catTrailItems]) => {
        const catData = submissionData[`cat${catNumber}`] || submissionData;
        const catName = catData.fuzzballer_name || `Cat ${catNumber}`;
        
        // Count trail items for this cat
        let trailWetCount = 0;
        let trailDryCount = 0;
        
        catTrailItems.forEach(item => {
          if (item.type === 'can') {
            trailWetCount += item.quantity;
          } else if (item.type === 'dry') {
            trailDryCount += item.quantity;
          }
        });
        
        // Check if we already have a carrier for this cat from bundles
        const hasCarrierAlready = carrierProductsByCat[catNumber];
        
        // Add trail items for this cat
        catTrailItems.forEach((item, index) => {
          // Determine product type for trail items - check both 'type' and 'product_type' fields
          const productType = item.type || item.product_type || 'can'; // Default to 'can' for trail items
          
          // Build condensed property string with product type
          const qdata = `q${quizId}|${catNumber}|T|${productType}`;
          
          // If no bundle carrier exists and this is the first trail item, make it the carrier
          const isCarrier = !hasCarrierAlready && index === 0;
          
          let itemProperties;
          
          if (isCarrier) {
            // Build full quiz data for trail carrier
            const fullQuizData = {
              // Customer data
              customer_name: submissionData.customer_name,
              customer_email: submissionData.customer_email,

              // Cat data
              cat_name: catName,
              breed: catData.breed,
              age_range: catData.age_range,
              lifestyle: catData.lifestyle,
              wet_dry_preference: catData.wet_dry_preference,
              cans_per_day: catData.cans_per_day || '',
              health_concerns_exists: catData.health_concerns_exists,
              health_concerns: catData.health_concerns,
              disliked_flavors: catData.disliked_flavors || [],

              // Order metadata
              is_multi_cat: isMultiCat,
              cat_count: submissionData.catCount || 1,
              trail_summary: `${trailWetCount} cans, ${trailDryCount} bags`,
              timestamp: Date.now()
            };
            
            itemProperties = {
              '_qdata': qdata,
              '_quiz_full_data': JSON.stringify(fullQuizData),
              '_group_title': isMultiCat ? `${catName}'s Trial Box` : "Your Cat's Trial Box",
              'Trial Box': 'true',
              'Fuzzballer name': catName
            };
            
            carrierProductsByCat[catNumber] = true;
            console.log(`📦 TRAIL CARRIER assigned for cat ${catNumber}:`, item.variantId || item.variant_id);
          } else {
            // Non-carrier trial items get minimal properties
            itemProperties = {
              '_qdata': qdata,
              'Trial Box': 'true'
            };
          }
          
          cartItems.push({
            id: String(item.variantId || item.variant_id),  // Ensure ID is a string
            quantity: item.quantity,
            selling_plan: null,
            source: 'trail',
            properties: itemProperties
          });
        });
      });
    }
    
    // Handle extras items - now per cat
    if (extrasItems && typeof extrasItems === 'object' && !Array.isArray(extrasItems)) {
      // New format: extrasItems is an object with cat keys
      Object.entries(extrasItems).forEach(([catKey, catExtras]) => {
        if (catExtras && catExtras.length > 0) {
          const catNumber = catKey.replace('cat', '');
          const catData = submissionData[`cat${catNumber}`] || submissionData;
          const catName = catData.fuzzballer_name || `Cat ${catNumber}`;
          
          // Check if we already have a carrier for this cat
          const hasCarrierAlready = carrierProductsByCat[catNumber];
          
          catExtras.forEach((item, index) => {
            // Determine product type for extras - check both 'type' and 'product_type' fields
            const productType = item.type || item.product_type || 'product';
            
            // Build condensed property string with product type
            const qdata = `q${quizId}|${catNumber}|E|${productType}`;
            
            // If no carrier exists yet and this is the first extra, make it the carrier
            const isCarrier = !hasCarrierAlready && index === 0;
            
            let itemProperties;
            
            if (isCarrier) {
              // Build full quiz data for extras carrier
              const fullQuizData = {
                // Customer data
                customer_name: submissionData.customer_name,
                customer_email: submissionData.customer_email,

                // Cat data
                cat_name: catName,
                breed: catData.breed,
                age_range: catData.age_range,
                lifestyle: catData.lifestyle,
                wet_dry_preference: catData.wet_dry_preference,
                cans_per_day: catData.cans_per_day || '',
                health_concerns_exists: catData.health_concerns_exists,
                health_concerns: catData.health_concerns,
                disliked_flavors: catData.disliked_flavors || [],

                // Order metadata
                is_multi_cat: isMultiCat,
                cat_count: submissionData.catCount || 1,
                extras_count: catExtras.length,
                timestamp: Date.now()
              };
              
              itemProperties = {
                '_qdata': qdata,
                '_quiz_full_data': JSON.stringify(fullQuizData),
                '_group_title': isMultiCat ? `${catName}'s Extras` : "Your Cat's Extras",
                '_original_price': item.price ? `£${item.price.toFixed(2)}` : '',
                '_discount_percentage': '50',
                'Fuzzballer name': catName
              };
              
              carrierProductsByCat[catNumber] = true;
              console.log(`📦 EXTRAS CARRIER assigned for cat ${catNumber}:`, item.variantId || item.variant_id);
            } else {
              // Non-carrier extras get minimal properties
              itemProperties = {
                '_qdata': qdata,
                '_discount_percentage': '50'
              };
            }
            
            cartItems.push({
              id: String(item.variantId || item.variant_id),  // Ensure ID is a string
              quantity: 1,
              selling_plan: null,
              source: 'extras',
              properties: itemProperties
            });
          });
        }
      });
    } else if (Array.isArray(extrasItems) && extrasItems.length > 0) {
      // Legacy format: single array of extras (assign to cat 1)
      const firstCatData = submissionData.cat1 || submissionData;
      const catName = firstCatData.fuzzballer_name || 'Your Cat';
      const catNumber = '1';
      
      // Check if we already have a carrier for cat 1
      const hasCarrierAlready = carrierProductsByCat[catNumber];
      
      extrasItems.forEach((item, index) => {
        // Determine product type for extras - check both 'type' and 'product_type' fields
        const productType = item.type || item.product_type || 'product';
        
        // Build condensed property string with product type
        const qdata = `q${quizId}|${catNumber}|E|${productType}`;
        
        // If no carrier exists yet and this is the first extra, make it the carrier
        const isCarrier = !hasCarrierAlready && index === 0;
        
        let itemProperties;
        
        if (isCarrier) {
          // Build full quiz data
          const fullQuizData = {
            customer_name: submissionData.customer_name,
            customer_email: submissionData.customer_email,
            cat_name: catName,
            breed: firstCatData.breed,
            age_range: firstCatData.age_range,
            lifestyle: firstCatData.lifestyle,
            wet_dry_preference: firstCatData.wet_dry_preference,
            cans_per_day: firstCatData.cans_per_day || '',
            health_concerns_exists: firstCatData.health_concerns_exists,
            health_concerns: firstCatData.health_concerns,
            disliked_flavors: firstCatData.disliked_flavors || [],
            is_multi_cat: false,
            cat_count: 1,
            extras_count: extrasItems.length,
            timestamp: Date.now()
          };
          
          itemProperties = {
            '_qdata': qdata,
            '_quiz_full_data': JSON.stringify(fullQuizData),
            '_group_title': "Your Cat's Extras",
            '_original_price': item.price ? `£${item.price.toFixed(2)}` : '',
            '_discount_percentage': '50',
            'Fuzzballer name': catName
          };
          
          carrierProductsByCat[catNumber] = true;
        } else {
          itemProperties = {
            '_qdata': qdata,
            '_discount_percentage': '50'
          };
        }
        
        cartItems.push({
          id: String(item.variantId || item.variant_id),  // Ensure ID is a string
          quantity: 1,
          selling_plan: null,
          source: 'extras',
          properties: itemProperties
        });
      });
    }
    
    // Add discount carrier product if configured (once per quiz, not per cat)
    const quizContainer = document.querySelector('.fuzzball-quiz-container');
    const discountCarrierData = quizContainer?.getAttribute('data-discount-carrier-product');
    
    if (discountCarrierData) {
      try {
        const discountProduct = JSON.parse(discountCarrierData);
        
        if (discountProduct.variant_id) {
          console.log('💰 Adding discount carrier product:', discountProduct);
          
          // Build minimal properties for the discount carrier
          const qdata = `q${quizId}|DC`; // DC = Discount Carrier
          
          cartItems.push({
            id: String(discountProduct.variant_id),
            quantity: 1,
            selling_plan: null,
            source: 'discount_carrier',
            properties: {
              '_qdata': qdata,
              '_quiz_id': quizId,
              '_is_discount_carrier': 'true'
            }
          });

          // Store variant ID so cart/dropdown can re-add if missing at checkout
          localStorage.setItem('fuzzball_discount_carrier_variant', String(discountProduct.variant_id));

          console.log('✅ Discount carrier product added to cart items');
        }
      } catch (error) {
        console.error('Failed to parse discount carrier product data:', error);
      }
    }

    // Add free gift product if configured (once per quiz, not per cat)
    const freeGiftData = quizContainer?.getAttribute('data-free-gift-product');

    if (freeGiftData) {
      try {
        const freeGiftProduct = JSON.parse(freeGiftData);

        if (freeGiftProduct.variant_id) {
          console.log('🎁 Adding free gift product:', freeGiftProduct);

          // Build minimal properties for the free gift
          const qdata = `q${quizId}|FG`; // FG = Free Gift

          cartItems.push({
            id: String(freeGiftProduct.variant_id),
            quantity: 1,
            selling_plan: null,
            source: 'free_gift',
            properties: {
              '_qdata': qdata,
              '_quiz_id': quizId,
              '_is_free_gift': 'true'
            }
          });

          console.log('✅ Free gift product added to cart items');
        }
      } catch (error) {
        console.error('Failed to parse free gift product data:', error);
      }
    }

    return cartItems;
  },

  // Calculate bundle price from items
  calculateBundlePrice(bundleItems) {
    return bundleItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  },

  // Check if cart has items from quiz
  async hasQuizItems(quizId) {
    try {
      const cart = await this.getCart();
      return cart.items.some(item => {
        const qdata = item.properties?._qdata;
        if (!qdata) {
          // Fallback to old format
          return item.properties && item.properties._quiz_id === quizId;
        }
        // Parse new format
        const [qid] = qdata.split('|');
        return qid === `q${quizId}`;
      });
    } catch (error) {
      return false;
    }
  },

  // Get quiz data from cart
  async getQuizDataFromCart(quizId) {
    try {
      const cart = await this.getCart();
      
      // Find cart item with full quiz data (carrier product)
      let cartItem = cart.items.find(item => {
        const qdata = item.properties?._qdata;
        if (!qdata) {
          // Fallback to old format
          return item.properties?._quiz_id === quizId;
        }
        // Check if this is a quiz item and has full data
        const [qid] = qdata.split('|');
        return qid === `q${quizId}` && item.properties?._quiz_full_data;
      });
      
      // If no carrier found, try old format
      if (!cartItem) {
        cartItem = cart.items.find(item => item.properties?._quiz_id === quizId);
      }
      
      if (!cartItem || !cartItem.properties) {
        return null;
      }
      
      const props = cartItem.properties;
      
      // Try to extract from new format first
      if (props._quiz_full_data) {
        try {
          const fullData = JSON.parse(props._quiz_full_data);
          return {
            fuzzballer_name: fullData.cat_name || '',
            breed: fullData.breed || '',
            age_range: fullData.age_range || '',
            wet_dry_preference: fullData.wet_dry_preference || '',
            cans_per_day: fullData.cans_per_day || '',
            health_concerns_exists: fullData.health_concerns_exists || '',
            health_concerns: fullData.health_concerns || '',
            lifestyle: fullData.lifestyle || '',
            disliked_flavors: fullData.disliked_flavors || [],
            customer_name: fullData.customer_name || '',
            customer_email: fullData.customer_email || ''
          };
        } catch (e) {
          console.error('Failed to parse _quiz_full_data:', e);
        }
      }
      
      // Fallback to old format
      return {
        fuzzballer_name: props._cat_name || '',
        breed: props._quiz_breed || '',
        age_range: props._quiz_age_range || '',
        wet_dry_preference: props._quiz_wet_dry_preference || '',
        health_concerns_exists: props._quiz_health_concerns_exists || '',
        health_concerns: props._quiz_health_concerns || '',
        lifestyle: props._quiz_lifestyle || '',
        disliked_flavors: props._quiz_disliked_flavors ? JSON.parse(props._quiz_disliked_flavors) : [],
        customer_name: props._quiz_customer_name || '',
        customer_email: props._quiz_customer_email || ''
      };
    } catch (error) {
      return null;
    }
  },

  // Get selected products from cart
  async getSelectedProductsFromCart(quizId, source) {
    try {
      const cart = await this.getCart();
      
      // Map source to single letter format
      const sourceMap = {
        'bundle': 'B',
        'trail': 'T',
        'extras': 'E'
      };
      const sourceChar = sourceMap[source] || source;
      
      const products = cart.items
        .filter(item => {
          const qdata = item.properties?._qdata;
          if (!qdata) {
            // Fallback to old format
            return item.properties?._quiz_id === quizId && item.properties?._source === source;
          }
          // Parse new format
          const [qid, , src] = qdata.split('|');
          return qid === `q${quizId}` && src === sourceChar;
        })
        .map(item => ({
          id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          title: item.product_title,
          type: item.properties._product_type || null
        }));
      
      return products;
    } catch (error) {
      return [];
    }
  }
};